// How long a source video is, and what shape, kept where the FIRST render can
// read it.
//
// The frames live in IndexedDB (services/mediaExtractor), which is the right
// home for megabytes of blobs but cannot answer during a render — it is
// asynchronous by design. The timeline needs the duration before it paints
// anything: it sizes the ruler and decides the Fit zoom, so not having it means
// laying the whole panel out against DEFAULT_DURATION and snapping to the real
// length a few frames later, with every clip and tick jumping as it does.
//
// Two numbers per video is exactly what synchronous storage is for. A video
// never changes — a new one is uploaded to a new S3 key, which is a new entry
// here — so what was written on an earlier visit is still true.

import { normalizeCacheKey } from '@src/services/mediaExtractor';

const STORAGE_KEY = 'vbee.timeline.video-meta';

// Roughly 60 bytes an entry, so this is a rounding error against the 5MB
// localStorage gets. Oldest out first, which for this is last-written order.
const MAX_ENTRIES = 100;

// Disabled storage (private mode, a blocked origin) means no cache, not an
// error: every caller treats a miss as "not known yet" and carries on.
const readAll = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeAll = (entries) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    // Full or unavailable — the timeline just measures the video itself again.
  }
};

/**
 * What is known about `url`'s video, or null. Synchronous, so a render can use
 * it. Keyed past the presigned signature, which changes per request.
 *
 * @returns {{ durationSec: number, aspect: number } | null}
 */
export const readVideoMeta = (url) => {
  if (!url) return null;
  const entry = readAll()[normalizeCacheKey(url)];
  return entry && entry.durationSec > 0 ? entry : null;
};

/**
 * Note what the extraction has just measured. Ignores a duration of zero, which
 * only ever means "not known yet" — writing that would teach the next visit
 * nothing and cost it a re-measure anyway.
 */
export const rememberVideoMeta = (url, { durationSec, aspect }) => {
  if (!url || !(durationSec > 0)) return;
  const key = normalizeCacheKey(url);
  const entries = readAll();
  // Re-inserted rather than updated in place, so a video still being opened
  // stays at the young end of the list and outlives one nobody has touched.
  delete entries[key];
  entries[key] = { durationSec, aspect: aspect > 0 ? aspect : undefined };
  const keys = Object.keys(entries);
  keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES)).forEach((old) => {
    delete entries[old];
  });
  writeAll(entries);
};
